import type { StepKey } from '../state/ProgressContext';
import { HVAC_FIELD_ROLE_ID, HVAC_REMOTE_ROLE_ID, HVAC_WHAT_YOU_SELL, type WorkMode } from './hvac';

export interface PositionStep {
  key: StepKey;
  label: string;
  route: string;
}

export interface PositionDefinition {
  id: string;
  roleId: string;
  workMode: WorkMode;
  product: 'AI_RECEPTIONIST' | 'HVAC_SPEED_TO_LEAD';
  listing: {
    title: string;
    location: string;
    type: string;
    summary: string;
    tags: string[];
    description: string[];
    responsibilities: string[];
    qualifications: string[];
    compensationSummary?: string;
  };
  application: {
    questions: string[];
    experienceScreening?: {
      yearsQuestion: string;
      yearsOptions: string[];
      descriptionQuestion: string;
    };
  };
  documents: {
    agreement: { title: string; templateUrl: string; draftUrl: string; signedUrl: string; fieldsUrl: string };
    taxForm: { title: string; templateUrl: string; draftUrl: string; signedUrl: string; fieldsUrl: string };
  };
  workflow: Array<{ name: string; steps: PositionStep[] }>;
}

export const salesContractorPosition: PositionDefinition = {
  id: 'sales-contractor',
  roleId: 'independent_sales_contractor',
  workMode: 'REMOTE',
  product: 'AI_RECEPTIONIST',
  listing: {
    title: 'AI Receptionist — Remote Sales Representative',
    location: 'Remote',
    type: 'Independent contractor',
    summary: 'Call U.S. small businesses from the leads we provide, introduce the Krovance AI receptionist, and guide interested businesses through the next step.',
    tags: ['Remote', 'Flexible schedule', 'Commission-based'],
    compensationSummary: 'Commission-only. You start at $200 USD per closed, paid client, your commission increases by $25 USD after every 5 closed, paid clients, and the maximum is $300 USD per closed, paid client. Commission is paid only after the client’s payment successfully clears. There is no hourly wage, no salary, no draw, and no guaranteed income.',
    description: [
      'This is an ongoing 1099 independent contractor opportunity, not a W-2 employment position. You control your own schedule, subject to Krovance’s professional conduct, confidentiality, lead-protection, recordkeeping, and client-protection requirements.',
      'Compensation: You start at $200 USD per closed, paid client. Your commission increases by $25 USD after every 5 closed, paid clients, up to a maximum of $300 USD per closed, paid client. Commission is paid only after the client’s payment successfully clears. This is commission-only — there is no hourly wage, no salary, no draw, and no guaranteed income, and there is no pay per call, lead, or appointment. Commission is earned only when a lead you brought in becomes a closed, paid client.',
      'You will represent the Krovance AI receptionist. It answers incoming calls, handles common customer questions, captures leads, assists with scheduling, reduces missed opportunities when staff cannot answer, and improves customer communication.',
      'You contact U.S. small businesses by phone and other approved outreach methods using Krovance’s lead-generation dashboard. You may find additional leads yourself, but every lead must be entered into the shared records we provide before you contact the business.',
      'To stay active, representatives are expected to keep lead records and call results current and make at least 50 calls per week (the required minimum). The target is 75–100 calls per week, and 100+ calls per week is strong performance.',
      'We also value feedback from sales representatives. If leads are not converting, we want feedback explaining why—for example, the wrong target business, an unclear offer, a pricing concern, lack of interest, or a service mismatch—so we can improve the offer and outreach process.',
      'Applicable tax documentation must be completed before any commission is paid out.',
      'Please confirm that you understand the commission-only structure and are comfortable making outbound calls to U.S. small businesses during U.S. business hours.',
    ],
    responsibilities: [
      'Select and claim available businesses from the Krovance lead pool in the lead-generation dashboard.',
      'Contact businesses by phone and other approved outreach methods.',
      'Speak with the decision-maker, or request them when they are unavailable.',
      'Ask discovery questions to understand how the business handles its calls today.',
      'Explain the assigned AI service clearly and accurately.',
      'Send approved follow-up emails to interested businesses.',
      'Record call outcomes and objections in the lead records after every attempt.',
      'Follow up with interested leads.',
      'Forward serious opportunities or advanced questions to Krovance.',
      'Share feedback on why leads do or do not convert.',
    ],
    qualifications: [
      'At least 18 years old.',
      'No prior sales experience is strictly required — entry-level applicants are welcome to apply.',
      'Strong communication and listening skills.',
      'Comfortable speaking with business owners or managers.',
      'Comfortable with commission-only compensation.',
      'Able to work independently.',
      'Basic smartphone and computer skills.',
      'Interest in sales, business, AI, or technology.',
      'Helpful but not required: prior B2B, remote sales, SaaS, merchant services, POS, telecom, payroll, insurance, marketing, or similar experience.',
    ],
  },
  application: {
    questions: [
      'How many outbound calls can you realistically aim for per week? The required minimum is 50 calls per week, the target is 75–100, and 100+ is strong performance - you can freely go beyond that.',
      'Do you have experience tracking leads in Google Sheets or a CRM?',
      'Are you able to start ASAP? If not, what date would you be able to start?',
      'Do you have any prior experience making sales calls? How would you describe your performance?',
      'Do you know how to use LLMs such as ChatGPT or Claude to ask questions, request advice, and review information? If not, are you willing to learn?\n\nWe use AI for mentoring and giving advice to sales representatives. We also suggest using AI to summarize documents, extract specific details, save reading time, and improve efficiency.',
      'We would like to hear a mock call so we can evaluate whether your tone, demeanor, fluency, and wording fit Krovance. Do you have a recording from a previous sales position or a mock call made through a service such as Salesforce or Yoodli? Providing one will greatly speed up your application process.',
    ],
  },
  documents: {
    agreement: { title: 'Contractor Agreement', templateUrl: '/api/documents/template/agreement', draftUrl: '/api/documents/draft/agreement', signedUrl: '/api/documents/signed/agreement', fieldsUrl: '/api/documents/fields/agreement' },
    taxForm: { title: 'Tax Form', templateUrl: '/api/documents/template/tax-form', draftUrl: '/api/documents/draft/tax-form', signedUrl: '/api/documents/signed/tax-form', fieldsUrl: '/api/documents/fields/tax-form' },
  },
  workflow: [
    { name: 'Application', steps: [
      { key: 'LOCATION', label: 'Location', route: '/onboarding/country' },
      { key: 'INITIAL_NOTICE', label: 'Initial Notice', route: '/onboarding/notice' },
      { key: 'APPLICATION_QUESTIONS', label: 'Application Questions', route: '/onboarding/application' },
      { key: 'APPLICATION_REVIEW', label: 'Application Review', route: '/onboarding/application' },
    ] },
    { name: 'Agreement', steps: [
      { key: 'SALES_RESOURCES', label: 'Sales Representative Resources', route: '/onboarding/resources' },
      { key: 'AGREEMENT', label: 'Contractor Agreement', route: '/onboarding/agreement' },
      { key: 'TAX_FORM', label: 'Tax Form', route: '/onboarding/tax-form' },
    ] },
    { name: 'Onboarding', steps: [
      { key: 'ONBOARDING_WELCOME', label: 'First Lead Training', route: '/onboarding/training' },
      { key: 'COMPLETE', label: 'Portal', route: '/onboarding/final' },
    ] },
  ],
};

export const fieldSalesPosition: PositionDefinition = {
  ...salesContractorPosition,
  id: 'field-commission-based-sales-rep',
  roleId: 'field_commission_sales_rep',
  workMode: 'FIELD',
  listing: {
    ...salesContractorPosition.listing,
    title: 'AI Receptionist — Field Sales Representative',
    // Worked on foot from a local lead pool, so it is offered only where Krovance has that
    // coverage. The markets are defined once, on the server (server/src/lib/fieldMarkets.ts):
    // that same list gates the application at the Location step, and the {MARKETS...} tokens
    // below are filled from it wherever this copy is rendered - so adding a market is one edit,
    // not a hunt through the prose (see client/src/lib/fieldMarkets.ts).
    // The meta row is a short chip beside "Independent contractor", so the markets themselves
    // are named in the summary directly under it rather than crammed in here.
    location: 'In person · select U.S. metro areas',
    summary: 'Visit the local small businesses we assign you, introduce the Krovance AI receptionist to owners and managers in person, and guide interested businesses through the next step. Open only in {MARKETS}.',
    tags: ['In person', 'Flexible schedule', 'Commission-based'],
    compensationSummary: 'Commission-only. You start at $300 USD per closed, paid client, your commission increases by $25 USD after every 5 closed, paid clients, and the maximum is $400 USD per closed, paid client. Commission is paid only after the client’s payment successfully clears. There is no hourly wage, no salary, no draw, and no guaranteed income.',
    description: [
      'This is an ongoing 1099 independent contractor opportunity, not a W-2 employment position. You visit local small businesses in person and control your own schedule, subject to Krovance’s professional conduct, confidentiality, lead-protection, recordkeeping, and client-protection requirements.',
      'Where this position is open: this is in-person work from our local lead pool, so we are hiring for it only in {MARKETS_FULL_AND}. You must live in or near one of these areas to apply — you choose yours on the first step of the application, and applications from anywhere else cannot be accepted right now.',
      'Compensation: You start at $300 USD per closed, paid client. Your commission increases by $25 USD after every 5 closed, paid clients, up to a maximum of $400 USD per closed, paid client. Commission is paid only after the client’s payment successfully clears. This is commission-only — there is no hourly wage, no salary, no draw, and no guaranteed income, and there is no pay per visit, lead, or appointment. Commission is earned only when a business you brought in becomes a closed, paid client.',
      'You will represent the Krovance AI receptionist. It answers incoming calls, handles common customer questions, captures leads, assists with scheduling, reduces missed opportunities when staff cannot answer, and improves customer communication.',
      'Krovance provides a lead pool. Field representatives select and claim available businesses before visiting them, so you are never cold-searching for who to see next — and a business you claim cannot be claimed by another representative.',
      'To stay active, representatives are expected to document every visit and aim for at least 15 documented in-person business visits per week.',
      'Representatives are responsible for their own transportation and local travel. Mileage, fuel, meals, transportation, and other personal expenses are not reimbursed.',
      'No prior sales experience is required. We provide the training, materials, and leads you need to get started.',
      'Applicable tax documentation must be completed before any commission is paid out.',
      'Please confirm that you understand the commission-only structure and that you are comfortable visiting local businesses in person.',
    ],
    responsibilities: [
      'Select and claim available businesses from the Krovance lead pool.',
      'Visit those businesses in person.',
      'Ask for the owner, manager, or decision-maker.',
      'Ask discovery questions to understand how the business handles its calls today.',
      'Explain the AI receptionist clearly and accurately.',
      'Show the product page, QR code, leave-behind flyer, and the live AI Receptionist demo line.',
      'Document the result of every visit, including objections and interest level.',
      'Record the follow-up or return visit you agreed to, then work it from your Follow-Ups queue in the portal.',
      'Forward serious opportunities or advanced questions to Krovance.',
    ],
    qualifications: [
      'At least 18 years old.',
      'No prior sales experience is strictly required — entry-level applicants are welcome to apply.',
      'Strong communication and listening skills.',
      'You live in or near one of the areas this position is open in: {MARKETS_FULL_OR}.',
      'Comfortable speaking with business owners or managers in person.',
      'Comfortable with commission-only compensation.',
      'Able to work independently.',
      'Reliable transportation for local travel between businesses.',
      'Basic smartphone and computer skills.',
      'Interest in sales, business, AI, or technology.',
      'Helpful but not required: prior B2B, field sales, SaaS, merchant services, POS, telecom, payroll, insurance, marketing, or similar experience.',
    ],
  },
  application: {
    ...salesContractorPosition.application,
    questions: [
      'How many in-person business visits can you realistically aim for per week? We expect around 15 visits per week to stay active, but you can freely go beyond that.',
      salesContractorPosition.application.questions[1]!,
      salesContractorPosition.application.questions[2]!,
      'Do you have reliable transportation to visit local businesses in person?',
      'How far are you willing to travel for in-person business outreach? Depending on the leads you pick, it could vary between 5-30 minutes travel.',
      'Are you comfortable walking into businesses and speaking directly with owners or managers?',
      'Briefly describe any sales, customer service, canvassing, or face-to-face experience you have.',
      salesContractorPosition.application.questions[4]!,
      salesContractorPosition.application.questions[5]!,
    ],
  },
};

// The HVAC Speed-to-Lead positions. Their job pages are built from content/hvac.tsx (JobDetail
// renders the HVAC layout for them); the listing fields here are what the jobs feed card shows.
const hvacWorkflow: PositionDefinition['workflow'] = [
  { name: 'Application', steps: [
    { key: 'LOCATION', label: 'Location', route: '/onboarding/country' },
    { key: 'INITIAL_NOTICE', label: 'About this job', route: '/onboarding/notice' },
    { key: 'APPLICATION_QUESTIONS', label: 'Application questions', route: '/onboarding/application' },
    { key: 'APPLICATION_REVIEW', label: 'Application review', route: '/onboarding/application' },
  ] },
  { name: 'Onboarding', steps: [
    { key: 'SALES_RESOURCES', label: 'Accept the position', route: '/onboarding/resources' },
    { key: 'AGREEMENT', label: 'Contractor agreement', route: '/onboarding/agreement' },
    { key: 'TAX_FORM', label: 'Tax form', route: '/onboarding/tax-form' },
  ] },
  { name: 'Training', steps: [
    { key: 'ONBOARDING_WELCOME', label: 'Training and first lead', route: '/onboarding/training' },
    { key: 'COMPLETE', label: 'Active', route: '/onboarding/final' },
  ] },
];

export const hvacRemotePosition: PositionDefinition = {
  ...salesContractorPosition,
  id: 'hvac-speed-to-lead-remote-sales',
  roleId: HVAC_REMOTE_ROLE_ID,
  workMode: 'REMOTE',
  product: 'HVAC_SPEED_TO_LEAD',
  listing: {
    title: 'HVAC Speed-to-Lead — Remote Sales Representative',
    location: 'Remote',
    type: 'Independent contractor · commission-only',
    summary: 'Call HVAC businesses from the lead list we give you, explain our Speed-to-Lead system, and close customers. $300 for every customer you close who pays. No experience required - we train you.',
    tags: ['Remote', '$300 per closed customer', 'No experience required'],
    compensationSummary: '$300 per qualifying closed customer',
    description: [HVAC_WHAT_YOU_SELL],
    responsibilities: [],
    qualifications: [],
  },
  application: { questions: [] },
  workflow: hvacWorkflow,
};

export const hvacFieldPosition: PositionDefinition = {
  ...hvacRemotePosition,
  id: 'hvac-speed-to-lead-field-sales',
  roleId: HVAC_FIELD_ROLE_ID,
  workMode: 'FIELD',
  listing: {
    ...hvacRemotePosition.listing,
    title: 'HVAC Speed-to-Lead — Field Sales Representative',
    location: 'In person · select U.S. metro areas',
    summary: 'Visit HVAC businesses from the lead list we give you, show our Speed-to-Lead system to the owner, and close customers. $300 for every customer you close who pays. No experience required - we train you. Open only in {MARKETS}.',
    tags: ['In person', '$300 per closed customer', 'No experience required'],
  },
};

// Every position that exists, newest first. Which of them the public can see and apply to is the
// admin's Applications Open switch (GET /api/positions), never this list.
export const availablePositions = [hvacRemotePosition, hvacFieldPosition, fieldSalesPosition, salesContractorPosition];
export const listedPositions = availablePositions;
export const activePosition = hvacRemotePosition;
export function positionForRole(roleId?: string | null): PositionDefinition {
  return availablePositions.find((position) => position.roleId === roleId) ?? hvacRemotePosition;
}
export function isHvacPosition(roleId?: string | null): boolean {
  return positionForRole(roleId).product === 'HVAC_SPEED_TO_LEAD' && availablePositions.some((position) => position.roleId === roleId);
}
export function isFieldPosition(roleId?: string | null): boolean {
  return availablePositions.find((position) => position.roleId === roleId)?.workMode === 'FIELD';
}
